import React, { useState, useEffect } from 'react';
import styles from './access.module.css';
import { useParams } from 'react-router-dom';
import { AlertColor } from '@mui/material';
import axios from 'axios';
import useSWR from 'swr';
import { useNavigate, useLocation } from 'react-router-dom';
import FetchData from '../ServerCall/FetchData';
import { NotificationDialog, AcceptRejectDialog } from '../CommonFunctions/DialogComponents';
import { serverEndPoint } from '../ServerCall/EnvironmentVariables';
import '../allPages.css';
import { fontSize, palette } from '../..';
import ManageAccessHeader from './ManageAccessHeader';
import AddNewAccessModal from './AddNewAccessModal';
import AccessTable from './AccessTable';
import UserDeleteConfirmation from './UserDeleteConfirmation';
import { TAlert } from '../../types/types';

type SelectedUser = {
  id: string;
  name: string;
  emailId: string;
  isGroup: boolean;
  role: 'owner' | 'moderator' | 'creator' | 'custom_creator' | 'player';
};

type User = SelectedUser & {
  customPermissions?: [
    {
      contentTypeId: number;
      privilegeId: number;
    },
  ];
};
const Access = () => {
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [testMessage, setTestMessage] = useState<string>('');
  const [severity, setSeverity] = useState<AlertColor>('success');
  const [alert, setAlert] = useState<null | TAlert>(null);
  const [modalContent, setModalContent] = useState<any>('User');
  const [searchQuery, setSearchQuery] = useState<any>('');
  const [selectedUser, setSelectedUser] = useState<User | SelectedUser | null>(null);
  const [addedUser, setAddedUser] = useState<null | User>(null);
  const { parentId } = useParams();
  const [originalAccessList, setOriginalAccessList] = useState<any>([]);
  const [customPermissions, setCustomPermissions] = useState<any>({});
  const [showUserDeleteConfirmation, setShowUserDeleteConfirmation] = useState(false);
  const [user_GroupId, setUser_GroupID] = useState<any>();
  const [user_GroupName, setUser_GroupName] = useState<any>();
  const [isModalOpen, setIsModalOpen] = useState<any>(false);
  const [isInGroup, setIsInGroup] = useState(false);
  const [rowInEditMode, setRowInEditMode] = useState<null | User>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [sortField, setSortField] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: 'name', direction: 'asc' });

  const [currentRoleID, setCurrentRoleID] = useState<number>(8);
  const [disableOwnerAccess, setDisableOwnerAccess] = useState<boolean>(false);
  const [disableModeratorAccess, setDisableModeratorAccess] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  const handleBackButton = () => {
    navigate(-1);
  };

  let roles: any[] = [
    { id: 3, value: 'owner', displayValue: 'Owner' },
    { id: 4, value: 'moderator', displayValue: 'Moderator' },
    { id: 5, value: 'creator', displayValue: 'Creator' },
    { id: 6, value: 'custom_creator', displayValue: 'Custom Creator' },
    { id: 7, value: 'player', displayValue: 'Player' },
  ];

  const fetcher = (url: string) =>
    axios
      .get(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      })
      .then((res) => res.data);

  const { data: workspaces } = useSWR(`${serverEndPoint}workspace`, fetcher);

  const {
    data: accessList = [],
    mutate: mutateAccessList,
    isValidating,
  } = useSWR(`${serverEndPoint}workspace/access/${parentId}`, fetcher, {
    revalidateOnFocus: false,
  });

  let parentWorkspace = workspaces?.find((workspace: any) => workspace.id === parentId);

  const parentWorkspaceName = parentWorkspace?.name || location.state?.workspaceName || '';

  const disableAccessOptions = (workSpace: any) => {
    // if role is owner(roleId is 3) then user can't give anyone owner's access
    if (workSpace.roleId === 3) {
      setDisableOwnerAccess(true);
    }
    // if role is moderator(roleId is 4) then user can't give anyone owner's or moderator's access
    if (workSpace.roleId === 4) {
      setDisableOwnerAccess(true);
      setDisableModeratorAccess(true);
    }
  };

  useEffect(() => {
    let workSpaces: any = sessionStorage.getItem('workspaces');

    workSpaces = workSpaces ? JSON.parse(workSpaces) : [];

    let currentWorkspace = workSpaces.find((item: any) => item.id === parentId);

    if (!currentWorkspace) {
      workSpaces = sessionStorage.getItem('subWorkspaces');

      workSpaces = workSpaces ? JSON.parse(workSpaces) : [];

      currentWorkspace = workSpaces.find((item: any) => item.id === parentId);
    }

    setCurrentRoleID(currentWorkspace.roleId);
    disableAccessOptions(currentWorkspace);
  }, [parentId]);

  const handleNewButtonClick = () => {
    setIsModalOpen(true);
  };

  useEffect(() => { 
    // setIsLoading(false);
    if ( !addedUser && accessList.length > 0 && originalAccessList.length === 0) { 

      setOriginalAccessList(JSON.parse(JSON.stringify(accessList)));
      const permissions: any = {};
      accessList.forEach((access: any) => {
        if (access.customPermissions && access.customPermissions.length > 0) {
          permissions[access.id] = {};
          access.customPermissions.forEach((permission: any) => {
            permissions[access.id][permission.contentTypeId] = permission.privilegeId;
          });
        }
      });
      setCustomPermissions(permissions); // Set the customPermissions state based on accessList
    }
    setIsLoading(isValidating);
  }, [accessList]);

  const closeModal = () => {
    setIsModalOpen(false);
    setModalContent('');
    setSearchQuery('');
    setSelectedUser(null);
  };

  const handleAdd = () => {
    const updatedAccessList = [...accessList];
    const existingUser = updatedAccessList.find((item) => item.userGroupId === selectedUser?.id);
    if (!existingUser) {
      updatedAccessList.push({
        id: selectedUser?.id,
        name: selectedUser?.name,
        role: 'player',
        isGroup: selectedUser?.isGroup,
      });
      if (selectedUser) {
        const rowInfo = {
          id: selectedUser.id,
          name: selectedUser.name,
          emailId: selectedUser.emailId,
          isGroup: selectedUser.isGroup,
          role: 'player' as 'player',
        };

        setRowInEditMode(rowInfo);
        setAddedUser(rowInfo);
      }
    }
    mutateAccessList(updatedAccessList, false);

    closeModal();
  };

  const showAlert = (message: string, severity: AlertColor) => {
    setTestMessage(message);
    setSeverity(severity);
    setOpenAlert(true);
    setTimeout(() => {
      setOpenAlert(false);
    }, 3000);
  };

  const handleDeleteUser = async () => {
    const token = localStorage.getItem('accessToken');
    const userAdded = addedUser?.id === user_GroupId;
    const userOrGroup = accessList.find((item: any) => item.id === user_GroupId);
    if (userAdded) {
      //For deleting not saved but Added User
      let updatedAccessList = [...accessList];
      updatedAccessList = updatedAccessList.filter((item) => item.id !== user_GroupId);
      mutateAccessList(updatedAccessList, false);
      setRowInEditMode(null);
      setOriginalAccessList(JSON.parse(JSON.stringify(updatedAccessList)));
      showAlert(`${userOrGroup.name} deleted Successfully`, 'success');
      setAddedUser(null);
      setShowUserDeleteConfirmation(false);
    } else {
      try {
        setIsLoading(true);
        const userOrGroup = accessList.find((item: any) => item.id === user_GroupId); // If roleId or roleName are not available in accessList, fetch them dynamically
        let roleId;

        if (userOrGroup && userOrGroup.role) {
          roleId = roles.find((item: any) => item.value === userOrGroup.role)?.id;
        } else {
          // Default roleId if not available
          roleId = 7; // Assuming "player" as default
        }

        await FetchData({
          requestType: 'withData',
          method: 'DELETE', // Using DELETE method
          url: `workspace/access-deletion?workspaceId=${parentId}`,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            // "X-TENANT-ID": "silzila",
          },
          data: [
            {
              granteeId: user_GroupId,
              isGroup: isInGroup,
              roleId: roleId,
            },
          ],
        });
        let updatedAccessList = [...accessList];
        updatedAccessList = updatedAccessList.filter((item) => item.id != user_GroupId);
        mutateAccessList();
        setRowInEditMode(null);
        setOriginalAccessList(JSON.parse(JSON.stringify(updatedAccessList)));
        console.log('Access deleted');
        showAlert(`${userOrGroup.name} deleted Successfully`, 'success');
      } catch (error) {
        console.error('Error deleting access:', error);
        showAlert('Error deleting user. Try Again', 'error');
      } finally {
        setIsLoading(false);
        setShowUserDeleteConfirmation(false);
      }
    }
  };

  const handleAddButtonClick = () => {
    if (addedUser) return;

    if (rowInEditMode) {
      setAlert({
        message: 'There are unsaved changes. Can not proceed adding new user.',
        severity: 'error',
      });
      return;
    }

    handleNewButtonClick();
    setModalContent('User');
  };

  return (
    <div className="access-container" style={{ height: '100%', padding: '0 auto' }}>
      <div style={{ height: '8.6rem', overflowY: 'hidden' }}>
        <ManageAccessHeader />
        <div className="heading">
          <img
            src="/folder_work.svg"
            style={{ width: '24px', height: '24px' }}
            alt="Folder Icon"
          ></img>
          {/* <div> */}
          <h3 style={{ marginLeft: '6px', marginTop: '0' }}>{parentWorkspaceName}</h3>
          {/* </div> */}
        </div>
        <div
          className="subworkspace-action-buttons"
          style={{ marginTop: '1.45rem', height: '2rem' }}
        >
          <button
            className={styles.createWorkspace}
            onClick={handleAddButtonClick}
            style={{
              // backgroundColor: palette.primary.main,
              fontSize: fontSize.medium,
              padding: '0.5rem 1rem',
              cursor: 'pointer',
            }}
          >
            <img
              className={`${styles.adminAddIconDefault} addIconDefault`}
              src="/add_purple.png"
              alt="Add Users Logo"
              style={{ width: '16px', height: '16px' }}
            />
            <img
              className={`${styles.adminAddIconHover} addIconHover`}
              src="/add_white.png"
              alt="Add Users Logo"
              style={{ width: '16px', height: '16px' }}
            />
            Add
          </button>
        </div>
      </div>
      {/* style for 'access-table_loader_wrapper is defined in allPages.css */}
      <div className="access-table_loader_wrapper">
        {isLoading ? (
          <div
            className="loading-container"
            style={{
              height: '100%',
              width: '100%',
              border: '1px solid white',
              overflowY: 'auto',
              overflowX: 'hidden',
              marginTop: '0.5rem',
            }}
          >
            <div className="user-spinner"></div>
          </div>
        ) : accessList.length === 0 ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
              marginTop: '0.5rem',
            }}
          >
            <div
              style={{
                fontSize: fontSize.medium,
                color: palette.primary.contrastText,
              }}
            >
              Currently, no user/group have access to this particular workspace.
              <div>Please add user/groups via Add button.</div>
            </div>
          </div>
        ) : (
          // style for 'save_reload_table_wrapper' is in allPages.css
          <div className="save_reload_table_wrapper">
            <div
              className={`${styles.accesstableContainer} accesstableContainer`}
              style={{
                marginBottom: '0.2rem',
                height: 'calc(100% - 4.6rem)',
              }}
            >
              <AccessTable
                addedUser={addedUser}
                setAddedUser={setAddedUser}
                rowInEditMode={rowInEditMode}
                setRowInEditMode={setRowInEditMode}
                accessList={accessList}
                originalAccessList={originalAccessList}
                setOriginalAccessList={setOriginalAccessList}
                currentRoleID={currentRoleID}
                customPermissions={customPermissions}
                disableModeratorAccess={disableModeratorAccess}
                disableOwnerAccess={disableOwnerAccess}
                mutateAccessList={mutateAccessList}
                setCustomPermissions={setCustomPermissions}
                setIsInGroup={setIsInGroup}
                setShowUserDeleteConfirmation={setShowUserDeleteConfirmation}
                setUser_GroupID={setUser_GroupID}
                setUser_GroupName={setUser_GroupName}
              />
            </div>
          </div>
        )}
      </div>

      {showUserDeleteConfirmation ? (
        <UserDeleteConfirmation
          showUserDeleteConfirmation={showUserDeleteConfirmation}
          isInGroup={isInGroup}
          user_GroupName={user_GroupName}
          setShowUserDeleteConfirmation={setShowUserDeleteConfirmation}
          handleDeleteUser={handleDeleteUser}
        />
      ) : null}

      <AddNewAccessModal
        accessList={accessList}
        isModalForContentAccess={false}
        isModalOpen={isModalOpen}
        sortField={sortField}
        setSortField={setSortField}
        setSelectedUser={setSelectedUser}
        setSearchQuery={setSearchQuery}
        setModalContent={setModalContent}
        selectedUser={selectedUser}
        searchQuery={searchQuery}
        modalContent={modalContent}
        handleAdd={handleAdd}
        closeModal={closeModal}
        addedUser={addedUser}
      />

      {alert && (
        <NotificationDialog
          onCloseAlert={() => setAlert(null)}
          severity={alert.severity}
          testMessage={alert?.message}
          openAlert={!!alert}
        />
      )}

      <NotificationDialog
        openAlert={openAlert}
        severity={severity}
        testMessage={testMessage}
        onCloseAlert={() => {
          setOpenAlert(false);
          setTestMessage('');
        }}
      />
    </div>
  );
};

export default Access;
